import { Box, Button, Container, Paper, Typography } from "@mui/material";
import { DataTable } from "modules/common";
import { useState } from "react";
import DriverDialog from "./components/DriverDialog";
import { useCreateDriverMutation, useDeleteDriverMutation, useGetDriversQuery, useUpdateDriverMutation } from "api/adminSlice";
import { useTranslation } from "react-i18next";
import { cloneDeep, set } from "lodash";
import { Add } from "@mui/icons-material";
import moment from "moment";

const $COLUMN_WIDTH = 200
export default function AdminDrivers() {
    const { t } = useTranslation()

    const [page, setPage] = useState(1)

    //=================Driver API hooks==================
    const {
        data: drivers, isLoading: driversLoading, isFetching: driversFetching, refetch: refetchDrivers
    } = useGetDriversQuery({ page: 1, limit: 100 })

    const [createDriver,
        {
            isLoading: createDriverLoading,
            isSuccess: createDriverIsSuccess,
            error: createDriverError,
            reset: createReset
        }
    ] = useCreateDriverMutation()

    const [updateDriver,
        {
            isLoading: updateDriverLoading,
            isSuccess: updateDriverIsSuccess,
            error: updateDriverError,
            reset: updateReset
        }
    ] = useUpdateDriverMutation()

    const [deleteDriver,
        {
            isLoading: deleteDriverLoading,
            isSuccess: deleteDriverIsSuccess,
            error: deleteDriverError,
            reset: deleteReset
        }
    ] = useDeleteDriverMutation()

    //==========Get and navigate through service points==========
    const pageChangeHandler = ({ page }) => {
        if (!driversFetching || !driversLoading) {
            setPage(page)
        }
    }

    //===============Handle service point values change==============
    const [selectedDriver, setSelectedDriver] = useState({ name: "", phone: "", password: "" })

    const resetSelectedDriver = () => {
        setSelectedDriver({ name: "", phone: "", password: "" })
    }

    const handleChange = ({ target: { value, name } }) => {

        setSelectedDriver((prevState) => {
            let payload = cloneDeep(prevState)
            payload = set(payload, name, value)
            return payload
        })
    }

    //==============Driver dialog handlers==============
    const [isEditing, setIsEditing] = useState(false)

    const [openDriverDialog, setOpenDriverDialog] = useState(false)

    const resetAPIHooks = () => {
        createReset()
        updateReset()
        deleteReset()
    }

    const openDialogForCreateDriver = () => {
        resetAPIHooks()
        setIsEditing(false)
        setOpenDriverDialog(true)
    }

    const openDialogForUpdateDriver = (data) => {
        resetAPIHooks()
        data = cloneDeep(data)
        setSelectedDriver(data)
        setIsEditing(true)
        setOpenDriverDialog(true)
    }

    const closeDialogHandler = () => {
        setOpenDriverDialog(false)
        if (isEditing) {
            resetSelectedDriver()
        }
        setIsEditing(false)
    }


    //================Driver CRUD handlers===============
    const createDriverHandler = async (payload) => {
        let response = await createDriver(payload).unwrap()
        checkDriverActionSuccess(response)
    }
    const updateDriverHandler = async (payload) => {
        let response = await updateDriver(payload).unwrap()
        checkDriverActionSuccess(response)
    }
    const deleteDriverHandler = async (id) => {
        let response = await deleteDriver(id).unwrap()
        checkDriverActionSuccess(response)
    }
    const checkDriverActionSuccess = (response) => {
        if (response?.success) {
            refetchDrivers()
            setTimeout(() => {
                setOpenDriverDialog(false)
                resetSelectedDriver()
            }, 2000);
        }
    }

    const columns = [
        { field: "name", headerName: t("Full Name"), width: $COLUMN_WIDTH * 1.5, },
        { field: "phone", headerName: t("Phone"), renderCell: ({ value }) => (      <bdi>{value ? value.replace(/(\d{3})(\d{3})(\d{4})/, '$1 $2 $3') : ''}</bdi>), width: $COLUMN_WIDTH * 1.5 },
        { field: "createdAt", headerName: t("Created At"), valueGetter: ({ value }) => moment(value).format("DD/MM/YY - h:mm A"), width: $COLUMN_WIDTH * 1.5 },
    ];

    return (
        <Container>
            <Box display="flex" justifyContent="space-between" alignItems="center" my={25}>
                <Typography variant='h6'>{t("Drivers")}</Typography>
                <Button
                    startIcon={<Add />}
                    sx={{ textTransform: "none" }}
                    variant="contained"
                    onClick={openDialogForCreateDriver}
                >
                    {t("Add")}
                </Button>
            </Box>
            <Paper sx={{ height: "80vh" }}>
                <DataTable
                    onPaginationModelChange={pageChangeHandler}
                    paginationModel={{ page: page - 1, pageSize: 100 }}
                    rows={drivers?.docs ?? []}
                    columns={columns}
                    rowCount={drivers?.totalDocs ?? 0}
                    loading={driversLoading || driversFetching}
                    onRowClick={({ row }) => openDialogForUpdateDriver(row)}
                />
            </Paper>
            <DriverDialog
                openDriverDialog={openDriverDialog}
                loading={createDriverLoading || updateDriverLoading || deleteDriverLoading}
                isEditing={isEditing}
                driver={selectedDriver}
                handleChange={handleChange}
                onCreateDriver={createDriverHandler}
                onUpdateDriver={updateDriverHandler}
                onDeleteDriver={deleteDriverHandler}
                onCloseDialog={closeDialogHandler}
                error={createDriverError || updateDriverError || deleteDriverError}
                isSuccess={createDriverIsSuccess || updateDriverIsSuccess || deleteDriverIsSuccess}
            />
        </Container>
    )
}
